"use client";

import { Link, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import { getExpertAudioPlaybackUrl, getExpertWriteupPdfUrl } from "@/lib/expert-audio-client";
import { invokeEdgeFunction } from "@/lib/edge-functions";
import { DO_OR_DUMP_UPSELL_BUSINESS_PUSH, DO_OR_DUMP_UPSELL_NICHE } from "@/lib/do-or-dump-upsells";
import { ExpertVoiceRecorder } from "@/components/expert-audio/ExpertVoiceRecorder";

type ExpertUiStatus = "awaiting_audio" | "audio_submitted" | "writeup_ready";

function fmtShort(iso: string | null | undefined): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

function IncomingBubble({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`flex justify-start ${className}`}>
      <div className="max-w-[min(100%,18rem)] rounded-[20px] rounded-bl-[6px] bg-[#E9E9EB] px-3.5 py-2.5 text-[15px] leading-snug text-black shadow-sm">
        {children}
      </div>
    </div>
  );
}

function OutgoingBubble({ children }: { children: ReactNode }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[min(100%,18rem)] rounded-[20px] rounded-br-[6px] bg-[#007AFF] px-3.5 py-2.5 text-[15px] leading-snug text-white shadow-sm">
        {children}
      </div>
    </div>
  );
}

function CenterMeta({ children }: { children: ReactNode }) {
  return (
    <div className="flex justify-center py-1">
      <span className="rounded-full bg-black/[0.06] px-3 py-1 text-[11px] font-semibold text-zinc-600">{children}</span>
    </div>
  );
}

function OutgoingVoiceBubble({ orderId, durationLabel }: { orderId: string; durationLabel?: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    void getExpertAudioPlaybackUrl(orderId).then((url) => {
      if (cancelled) return;
      if (url) setSrc(url);
      else setErr("Could not load playback.");
    });
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  return (
    <OutgoingBubble>
      <p className="mb-2 text-[13px] font-semibold text-white/90">Voice memo</p>
      {durationLabel ? <p className="mb-2 text-[11px] text-white/70">{durationLabel}</p> : null}
      {err ? <p className="text-[12px] text-white/90">{err}</p> : null}
      {src ? (
        <audio controls preload="none" src={src} className="h-9 w-[min(100%,220px)] opacity-95 [&::-webkit-media-controls-panel]:bg-[#007AFF]">
          <track kind="captions" />
        </audio>
      ) : !err ? (
        <p className="text-[12px] text-white/80">Loading player…</p>
      ) : null}
    </OutgoingBubble>
  );
}

type Props = {
  orderId: string;
  lineSummary: string;
  customerEmail: string;
  hasBusinessPush: boolean;
  expertStatus: ExpertUiStatus | null;
  writeupMarkdown: string | null;
  audioSubmittedAt: string | null;
  writeupReadyAt: string | null;
  audioDurationSeconds: number | null;
};

export function ExpertChatView({
  orderId,
  lineSummary,
  customerEmail,
  hasBusinessPush,
  expertStatus,
  writeupMarkdown,
  audioSubmittedAt,
  writeupReadyAt,
  audioDurationSeconds,
}: Props) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(expertStatus);

  useEffect(() => {
    setStatus(expertStatus);
  }, [expertStatus]);

  const refresh = useCallback(() => {
    setStatus("audio_submitted");
    navigate(0);
  }, [navigate]);

  const [pdfLoading, setPdfLoading] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [upgradeError, setUpgradeError] = useState("");

  const pushPriceLabel = `$${DO_OR_DUMP_UPSELL_BUSINESS_PUSH.amountUsd.toFixed(2)}`;

  const handleUpgradeToPush = useCallback(async () => {
    setUpgradeError("");
    setUpgradeLoading(true);
    try {
      const data = await invokeEdgeFunction<{ url?: string; error?: string }>("stripe-checkout", {
        email: customerEmail.trim(),
        upsellNiche: false,
        upsellPush: true,
        attachPushCustomerOrderId: orderId,
        origin: window.location.origin,
      });
      if (data && typeof data === "object" && "error" in data && data.error) {
        setUpgradeError(data.error);
        return;
      }
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch {
      setUpgradeError("Network error.");
    } finally {
      setUpgradeLoading(false);
    }
  }, [customerEmail, orderId]);

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const url = await getExpertWriteupPdfUrl(orderId);
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    } finally {
      setPdfLoading(false);
    }
  };

  const priceLabel = `$${DO_OR_DUMP_UPSELL_NICHE.amountUsd.toFixed(2)}`;
  const durLabel =
    audioDurationSeconds != null && audioDurationSeconds > 0
      ? `${Math.floor(audioDurationSeconds / 60)}:${String(audioDurationSeconds % 60).padStart(2, "0")} long`
      : undefined;

  const showVoiceMemoBubble = status === "audio_submitted" || status === "writeup_ready";

  return (
    <div className="mx-auto flex max-w-md flex-col overflow-hidden rounded-[22px] border border-black/[0.06] bg-[#ECECED] shadow-[0_8px_40px_rgba(0,0,0,0.08)]">
      {/* iOS-ish nav */}
      <header className="flex shrink-0 items-center gap-1 border-b border-black/[0.06] bg-[#F7F7F8]/95 px-2 py-2 backdrop-blur-md">
        <Link
          to="/account/orders"
          className="rounded-lg px-2 py-2 text-[17px] font-normal text-[#007AFF] hover:bg-black/[0.04]"
        >
          ‹ Orders
        </Link>
        <div className="min-w-0 flex-1 text-center">
          <h1 className="truncate text-[17px] font-semibold leading-tight text-zinc-900">Expert review</h1>
          <p className="truncate text-[11px] text-zinc-500">Private · crshm · {lineSummary}</p>
        </div>
        <div className="w-14 shrink-0" aria-hidden />
      </header>

      {/* Thread */}
      <div className="max-h-[min(560px,calc(100dvh-14rem))] flex-1 space-y-3 overflow-y-auto px-3 py-4 [scrollbar-width:thin]">
        {!status ? (
          <IncomingBubble>
            <p className="text-[15px]">
              Hang tight — we&apos;re spinning up your thread. Pull to refresh doesn&apos;t exist yet, so tap{" "}
              <Link to="/account/orders" className="font-semibold text-[#007AFF]">
                Orders
              </Link>{" "}
              and open this again in a moment.
            </p>
          </IncomingBubble>
        ) : (
          <>
            <CenterMeta>Today</CenterMeta>
            <IncomingBubble>
              <p className="text-[15px]">
                You&apos;re in — thanks for grabbing the Expert Audit ({priceLabel}). This thread is where your{" "}
                <strong>voice memo</strong> lives and where we&apos;ll drop your <strong>written audit + PDF</strong> when it&apos;s
                ready.
              </p>
              <p className="mt-2 text-[13px] text-zinc-600">
                Tap the red mic below, speak up to 10 minutes (questions + context), then Send. Redo anytime before Send.
              </p>
            </IncomingBubble>

            {showVoiceMemoBubble ? (
              <>
                <CenterMeta>{fmtShort(audioSubmittedAt) || "Voice memo"}</CenterMeta>
                <OutgoingVoiceBubble orderId={orderId} durationLabel={durLabel} />
              </>
            ) : null}

            {status === "audio_submitted" ? (
              <IncomingBubble>
                <p className="text-[15px]">
                  <strong className="font-semibold">Got it.</strong> Your memo&apos;s in queue — we&apos;re drafting your writeup.
                  You&apos;ll see a PDF attachment here when it lands.
                </p>
              </IncomingBubble>
            ) : null}

            {status === "writeup_ready" && writeupMarkdown ? (
              <>
                <CenterMeta>{fmtShort(writeupReadyAt) || "Writeup"}</CenterMeta>
                <IncomingBubble>
                  <p className="mb-2 text-[14px] font-semibold text-zinc-900">Your audit (PDF)</p>
                  <button
                    type="button"
                    disabled={pdfLoading}
                    onClick={() => void downloadPdf()}
                    className="flex w-full items-center gap-3 rounded-2xl bg-white px-3 py-3 text-left shadow-[inset_0_0_0_1px_rgba(0,0,0,0.06)] transition active:scale-[0.99] disabled:opacity-60"
                  >
                    <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-[#FF3B30] text-[11px] font-black text-white">
                      PDF
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[15px] font-semibold text-zinc-900">Expert_writeup.pdf</p>
                      <p className="text-[12px] text-zinc-500">{pdfLoading ? "Opening…" : "Tap to open"}</p>
                    </div>
                    <span className="text-lg font-light text-zinc-400" aria-hidden>
                      ›
                    </span>
                  </button>
                </IncomingBubble>

                <IncomingBubble className="mt-1">
                  <p className="mb-2 text-[13px] font-semibold text-zinc-700">Message</p>
                  <div className="text-[14px] leading-relaxed text-zinc-900 [&_h2]:mt-3 [&_h2]:text-[15px] [&_h2]:font-bold [&_li]:mt-1 [&_ol]:mt-2 [&_ol]:list-decimal [&_ol]:pl-4 [&_p]:mt-2 [&_ul]:mt-2 [&_ul]:list-disc [&_ul]:pl-4">
                    <ReactMarkdown>{writeupMarkdown}</ReactMarkdown>
                  </div>
                </IncomingBubble>
              </>
            ) : null}
          </>
        )}
      </div>

      {/* Composer / upgrade */}
      <footer className="shrink-0 border-t border-black/[0.06] bg-[#F7F7F8]/98 p-3 pb-[max(12px,env(safe-area-inset-bottom))] backdrop-blur-md">
        {status === "awaiting_audio" ? (
          <ExpertVoiceRecorder orderId={orderId} locked={false} onSubmitted={refresh} variant="chat" />
        ) : status === "audio_submitted" ? (
          <div className="rounded-[28px] border border-black/[0.06] bg-white/90 px-4 py-3 text-center text-[13px] text-zinc-500">
            Voice memo sent. We&apos;ll reply in-thread when your audit is ready.
          </div>
        ) : status === "writeup_ready" ? (
          hasBusinessPush ? (
            <div className="space-y-2">
              <Link
                to={`/account/orders/${orderId}/push`}
                className="flex min-h-12 w-full items-center justify-center rounded-[28px] bg-[#007AFF] px-4 py-3 text-center text-[14px] font-semibold text-white shadow-sm transition hover:bg-[#0066DD]"
              >
                Open Business Push thread
              </Link>
              <p className="text-center text-[12px] leading-snug text-zinc-500">
                Private async chat — questions & weekly check-ins (no calls).
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-2xl border-2 border-dashed border-zinc-400/80 bg-gradient-to-b from-white to-zinc-100/95 px-4 py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]">
                <div className="flex items-start gap-3">
                  <div
                    className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-zinc-200/90 text-[20px]"
                    aria-hidden
                  >
                    🔒
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[15px] font-bold leading-snug text-zinc-900">Thread locked</p>
                    <p className="mt-1 text-[13px] leading-snug text-zinc-600">
                      Your Expert Audit includes the PDF and writeup above. Add{" "}
                      <strong className="font-semibold text-zinc-900">30-Day Business Push</strong> ({pushPriceLabel}) for a private
                      async thread, a simple 30-day action plan, and weekly check-ins.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  disabled={upgradeLoading}
                  onClick={() => void handleUpgradeToPush()}
                  className="mt-4 w-full rounded-xl bg-violet-600 py-3 text-[14px] font-bold text-white shadow-md transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {upgradeLoading ? "Opening checkout…" : `Upgrade — ${pushPriceLabel}`}
                </button>
                {upgradeError ? (
                  <p className="mt-2 text-center text-[12px] font-medium text-rose-600">{upgradeError}</p>
                ) : null}
              </div>
              <p className="text-center text-[11px] leading-snug text-zinc-500">
                Everything above stays yours — this unlocks the working conversation on this order after checkout.
              </p>
            </div>
          )
        ) : null}
      </footer>

    </div>
  );
}
